import { Injectable } from '@angular/core';

import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { ReportsActions } from '@actions/index'
import * as fromApp from '@reducers/index'
import { HttpApiService } from '@services/http-api.service';
import { FeedbackModel, RequestModel, LocationModel, BusinessModel, ReportSettingsModel, RatingPerLocationModel, ReviewCountPerLocationModel, FeedbackCountPerLocationModel, SendEngineEfficiencyPerLocationModel, AvergeContactSpeedPerLocationModel, FeedbackReviewPercentagePerLocationModel, StarsDistributionPerLocationModel, ReviewPerReviewSitePerLocationModel, SentimentCheckCountPerLocationModel, CampaignMetricsPerLocationModel, ClickedOnReviewPerLocationModel, GeneratedReportsModel, ScheduledReportModel } from '@models/index';
import { RatingPerReviewSitePerLocationModel } from '@models/ratingPerReviewSitePerLocation.model';
import { SentReviewRequestCountPerLocationModel } from '@models/sentReviewRequestCountPerLocation.model';
import { MessageService } from 'primeng/api';
import { ReportsFacadeService } from '@services/reports/reports-facade.service'

@Injectable()
export class ReportsEffects {

    getReportsSettingsByBusiness$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.getReportsSettingsByBusiness),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getSelectedBusinessProfile),
                (request: RequestModel, business: BusinessModel) => ({
                    ...request,
                    params: request.params + `&businessId=${business.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data) => data as ReportSettingsModel),
                        map(reportsSettings => ReportsActions.getReportsSettingsByBusinessSuccess({ reportsSettings })),
                        catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                    )
            )
        )
    );

    saveReportsSettingsByBusiness$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.saveReportsSettingsByBusiness),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getSelectedBusinessProfile),
                (request: RequestModel, business: BusinessModel) => ({
                    ...request,
                    params: request.params + `&businessId=${business.id}`,
                    payload: {
                        ...request.payload,
                        businessId: business.id
                    }
                })
            ),
            switchMap((request) =>
                this.httpApiService.postRequest(request)
                    .pipe(
                        map((data) => data as ReportSettingsModel),
                        map(reportsSettings => ReportsActions.saveReportsSettingsByBusinessSuccess({ reportsSettings })),
                        catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                    )
            )
        )
    );

    generateReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.generateReport),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.postRequest(request)
                    .pipe(
                        map((data) => data as GeneratedReportsModel),
                        map(generatedReport => ReportsActions.generateReportSuccess({ generatedReport })),
                        catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                    )
            )
        )
    );

    searchGeneratedReports = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.searchGeneratedReports),
            map(action => action.pages),
            switchMap((pages) =>
                this.reportsFacadeService.searchGeneratedReportsTotal(pages)
                    .pipe(
                        map(generatedReports => ReportsActions.searchGeneratedReportsSuccess({ ...generatedReports })),
                        catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                    )
            )
        )
    );

    searchScheduledReportsPerBusiness$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.searchScheduledReportsPerBusiness),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data) => data as ScheduledReportModel[]),
                        map(scheduledReports => ReportsActions.searchScheduledReportsPerBusinessSuccess({ scheduledReports })),
                        catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                    )
            )
        )
    );

    addScheduledReportToBusiness$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.addScheduledReportToBusiness),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.postRequest(request)
                    .pipe(
                        map((data) => data as ScheduledReportModel),
                        map(scheduledReport => ReportsActions.addScheduledReportToBusinessSuccess({ scheduledReport })),
                        catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                    )
            )
        )
    );

    editScheduledReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.editScheduledReport),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.postRequest(request)
                    .pipe(
                        map((data) => data as ScheduledReportModel),
                        map(scheduledReport => ReportsActions.editScheduledReportSuccess({ scheduledReport })),
                        catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                    )
            )
        )
    );

    deleteScheduledReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.deleteScheduledReport),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.postRequest(request)
                    .pipe(
                        map((data) => data as { data: string }),
                        map(id => ReportsActions.deleteScheduledReportSuccess({ id })),
                        catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                    )
            )
        )
    );

    deleteGeneratedReport = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.deleteGeneratedReport),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.postRequest(request)
                    .pipe(
                        map((data) => data as { data: string }),
                        map(deletedReportId => ReportsActions.deleteGeneratedReportSuccess({ deletedReportId })),
                        catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                    )
            )
        )
    );

    getRatingPerLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.getRatingPerLocation),
            withLatestFrom(this.store.select(fromApp.getSelectedBusinessProfile)),
            switchMap(([{ startDate, endDate }, business]) =>
                this.httpApiService.getRequest({
                    params: `business-report/general-average-rating?startTime=${startDate}&endTime=${endDate}&businessId=${business.id}`
                }).pipe(
                    map((data) => data as RatingPerLocationModel[]),
                    map(ratingPerLocation => ReportsActions.getRatingPerLocationSuccess({ ratingPerLocation })),
                    catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                )
            )
        )
    );

    getStarsCountPerLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.getStarsCountPerLocation),
            withLatestFrom(this.store.select(fromApp.getSelectedBusinessProfile)),
            switchMap(([{ startDate, endDate }, business]) =>
                this.httpApiService.getRequest({
                    params: `business-report/general-count-stars?startTime=${startDate}&endTime=${endDate}&businessId=${business.id}`
                }).pipe(
                    map((data) => data as StarsDistributionPerLocationModel[]),
                    map(starsCountPerLocation => ReportsActions.getStarsCountPerLocationSuccess({ starsCountPerLocation })),
                    catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                )
            )
        )
    );

    getReviewsPerLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.getReviewsPerLocation),
            withLatestFrom(this.store.select(fromApp.getSelectedBusinessProfile)),
            switchMap(([{ startDate, endDate }, business]) =>
                this.httpApiService.getRequest({
                    params: `business-report/general-review-count?startTime=${startDate}&endTime=${endDate}&businessId=${business.id}`
                }).pipe(
                    map((data) => data as ReviewCountPerLocationModel[]),
                    map(reviewsPerLocation => ReportsActions.getReviewsPerLocationSuccess({ reviewsPerLocation })),
                    catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                )
            )
        )
    );

    getFeedbacksPerLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.getFeedbacksPerLocation),
            withLatestFrom(this.store.select(fromApp.getSelectedBusinessProfile)),
            switchMap(([{ startDate, endDate }, business]) =>
                this.httpApiService.getRequest({
                    params: `business-report/left-feedback?startTime=${startDate}&endTime=${endDate}&businessId=${business.id}`
                }).pipe(
                    map((data) => data as FeedbackCountPerLocationModel[]),
                    map(feedbacksPerLocation => ReportsActions.getFeedbacksPerLocationSuccess({ feedbacksPerLocation })),
                    catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                )
            )
        )
    );

    getSendEngineEfficiencyPerLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.getSendEngineEfficiencyPerLocation),
            withLatestFrom(this.store.select(fromApp.getSelectedBusinessProfile)),
            switchMap(([{ startDate, endDate }, business]) =>
                this.httpApiService.getRequest({
                    params: `business-report/send-engine-efficiency?startTime=${startDate}&endTime=${endDate}&businessId=${business.id}`,
                    payload: { startDate, endDate }
                }).pipe(
                    map((data) => data as SendEngineEfficiencyPerLocationModel[]),
                    map(senEngineEfficiencyPerLocation => ReportsActions.getSendEngineEfficiencyPerLocationSuccess({ senEngineEfficiencyPerLocation })),
                    catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                )
            )
        )
    );

    getClickedOnReviewPerLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.getClickedOnReviewPerLocation),
            withLatestFrom(this.store.select(fromApp.getSelectedBusinessProfile)),
            switchMap(([{ startDate, endDate }, business]) =>
                this.httpApiService.getRequest({
                    params: `business-report/clicked-review-time?startTime=${startDate}&endTime=${endDate}&businessId=${business.id}`
                }).pipe(
                    map((data) => data as ClickedOnReviewPerLocationModel[]),
                    map(clickedOnReviewPerLocation => ReportsActions.getClickedOnReviewPerLocationSuccess({ clickedOnReviewPerLocation })),
                    catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                )
            )
        )
    );

    getAverageContactSpeedPerLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.getAverageContactSpeedPerLocation),
            withLatestFrom(this.store.select(fromApp.getSelectedBusinessProfile)),
            switchMap(([{ startDate, endDate }, business]) =>
                this.httpApiService.getRequest({
                    params: `business-report/average-contact-response?startTime=${startDate}&endTime=${endDate}&businessId=${business.id}`,
                    payload: { startDate, endDate },
                }).pipe(
                    map((data) => data as AvergeContactSpeedPerLocationModel[]),
                    map(averageContactSpeedPerLocation => ReportsActions.getAverageContactSpeedPerLocationSuccess({ averageContactSpeedPerLocation })),
                    catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                )
            )
        )
    );

    getFeedbackReviewPercentagePerLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.getFeedbackReviewPercentagePerLocation),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getSelectedBusinessProfile),
                (request: RequestModel, business: BusinessModel) => ({
                    ...request,
                    params: request.params + `&businessId=${business.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data) => data as FeedbackReviewPercentagePerLocationModel[]),
                        map(feedbackReviewPercentage => ReportsActions.getFeedbackReviewPercentagePerLocationSuccess({ feedbackReviewPercentage })),
                        catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                    )
            )
        )
    );

    getReviewPerReviewSitePerLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.getReviewPerReviewSitePerLocation),
            withLatestFrom(this.store.select(fromApp.getSelectedBusinessProfile)),
            switchMap(([{ startDate, endDate }, business]) =>
                this.httpApiService.getRequest({
                    params: `business-report/reviews-review-site?startTime=${startDate}&endTime=${endDate}&businessId=${business.id}`,
                    payload: { startDate, endDate },
                }).pipe(
                    map((data) => data as ReviewPerReviewSitePerLocationModel[]),
                    map(reviewsPerReviewSite => ReportsActions.getReviewPerReviewSitePerLocationSuccess({ reviewsPerReviewSite })),
                    catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                )
            )
        )
    );

    getRatingPerReviewSitePerLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.getRatingPerReviewSitePerLocation),
            withLatestFrom(this.store.select(fromApp.getSelectedBusinessProfile)),
            switchMap(([{ startDate, endDate }, business]) =>
                this.httpApiService.getRequest({
                    params: `business-report/average-rating-review-site?startTime=${startDate}&endTime=${endDate}&businessId=${business.id}`,
                    payload: { startDate, endDate },
                }).pipe(
                    map((data) => data as RatingPerReviewSitePerLocationModel[]),
                    map(ratingPerReviewSite => ReportsActions.getRatingPerReviewSitePerLocationSuccess({ ratingPerReviewSite })),
                    catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                )
            )
        )
    );

    getSentRequestCountPerLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.getSentRequestCountPerLocation),
            withLatestFrom(this.store.select(fromApp.getSelectedBusinessProfile)),
            switchMap(([{ startDate, endDate }, business]) =>
                this.httpApiService.getRequest({
                    params: `business-report/sent-review-requests?startTime=${startDate}&endTime=${endDate}&businessId=${business.id}`,
                    payload: { startDate, endDate }
                }).pipe(
                    map((data) => data as SentReviewRequestCountPerLocationModel[]),
                    map(sentRequestCount => ReportsActions.getSentRequestCountPerLocationSuccess({ sentRequestCount })),
                    catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                )
            )
        )
    );

    getSentimentCheckCountPerLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.getSentimentCheckCountPerLocation),
            withLatestFrom(this.store.select(fromApp.getSelectedBusinessProfile)),
            switchMap(([{ startDate, endDate }, business]) =>
                this.httpApiService.getRequest({
                    params: `business-report/stars-thumbs-faces-count?startTime=${startDate}&endTime=${endDate}&businessId=${business.id}`,
                    payload: { startDate, endDate }
                }).pipe(
                    map((data) => data as SentimentCheckCountPerLocationModel[]),
                    map(sentimentCheckCount => ReportsActions.getSentimentCheckCountPerLocationSuccess({ sentimentCheckCount })),
                    catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                )
            )
        )
    );

    sendTest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.sendTest),
            map(action => action.request),
            switchMap(request =>
                this.httpApiService.postRequest(request)
                    .pipe(
                        map(sendTest => ReportsActions.sendEmailTestSuccess({ sendTest })),
                        tap(data => this.launchToastMssg('success', 'Send Test', `The report test has been sent`)),
                        catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                    )
            )
        )
    );

    getCampaignMetricsPerLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.getCampaignMetricsPerLocation),
            withLatestFrom(this.store.select(fromApp.getSelectedBusinessProfile)),
            switchMap(([{ startDate, endDate }, business]) =>
                this.httpApiService.getRequest({
                    params: `business-report/campaign-metrics?startTime=${startDate}&endTime=${endDate}&businessId=${business.id}`,
                    payload: { startDate, endDate }
                }).pipe(
                    map((data) => data as CampaignMetricsPerLocationModel[]),
                    map(campaignMetrics => ReportsActions.getCampaignMetricsPerLocationSuccess({ campaignMetrics })),
                    catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                )
            )
        )
    );

    getCampaignMetricsForLocationCampaign$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.getCampaignMetricsForLocationCampaign),
            switchMap(({ startDate, endDate, campaignId, locationId }) =>
                this.httpApiService.getRequest({
                    params: `business-report/location/campaign-metrics?startTime=${startDate}&endTime=${endDate}&campaignId=${campaignId}&locationId=${locationId}`,
                    payload: { startDate, endDate }
                }).pipe(
                    map((data) => (Array.isArray(data) ? data : [...[], data]) as CampaignMetricsPerLocationModel[]),
                    map(campaignMetrics => ReportsActions.getCampaignMetricsPerLocationSuccess({ campaignMetrics })),
                    catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                )
            )
        )
    );

    getSendEngineEfficiencyForLocationCampaign$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.getSendEngineEfficiencyForLocationCampaign),
            switchMap(({ startDate, endDate, campaignId, locationId }) =>
                this.httpApiService.getRequest({
                    params: `business-report/location/send-engine-efficiency?startTime=${startDate}&endTime=${endDate}&campaignId=${campaignId}&locationId=${locationId}`,
                    payload: { startDate, endDate }
                }).pipe(
                    map((data) => (Array.isArray(data) ? data : [...[], data]) as SendEngineEfficiencyPerLocationModel[]),
                    map(senEngineEfficiencyPerLocation => ReportsActions.getSendEngineEfficiencyPerLocationSuccess({ senEngineEfficiencyPerLocation })),
                    catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                )
            )
        )
    );

    getSentRequestCountForLocationCampaign$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.getSentRequestCountForLocationCampaign),
            switchMap(({ startDate, endDate, campaignId, locationId }) =>
                this.httpApiService.getRequest({
                    params: `business-report/location/sent-review-requests?startTime=${startDate}&endTime=${endDate}&campaignId=${campaignId}&locationId=${locationId}`,
                    payload: { startDate, endDate }
                }).pipe(
                    map((data) => (Array.isArray(data) ? data : [...[], data]) as SentReviewRequestCountPerLocationModel[]),
                    map(sentRequestCount => ReportsActions.getSentRequestCountPerLocationSuccess({ sentRequestCount })),
                    catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                )
            )
        )
    );

    getClickedOnReviewForLocationCampaign$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.getClickedOnReviewForLocationCampaign),
            switchMap(({ startDate, endDate, campaignId, locationId }) =>
                this.httpApiService.getRequest({
                    params: `business-report/location/clicked-review-time?startTime=${startDate}&endTime=${endDate}&campaignId=${campaignId}&locationId=${locationId}`
                }).pipe(
                    map((data) => (Array.isArray(data) ? data : [...[], data]) as ClickedOnReviewPerLocationModel[]),
                    map(clickedOnReviewPerLocation => ReportsActions.getClickedOnReviewPerLocationSuccess({ clickedOnReviewPerLocation })),
                    catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                )
            )
        )
    );

    getFeedbacksPerLocationCampaign$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReportsActions.getFeedbacksPerLocationCampaign),
            switchMap(({ startDate, endDate, campaignId, locationId }) =>
                this.httpApiService.getRequest({
                    params: `business-report/location/left-feedback?startTime=${startDate}&endTime=${endDate}&campaignId=${campaignId}&locationId=${locationId}`,
                    payload: { startDate, endDate }
                }).pipe(
                    map((data) => (Array.isArray(data) ? data : [...[], data]) as FeedbackCountPerLocationModel[]),
                    map(feedbacksPerLocation => ReportsActions.getFeedbacksPerLocationSuccess({ feedbacksPerLocation })),
                    catchError(error => of(ReportsActions.reportsError({ error: error.message })))
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private httpApiService: HttpApiService,
        private store: Store<any>,
        private messageToast: MessageService,
        private reportsFacadeService: ReportsFacadeService,
    ) { }

    launchToastMssg(severity: string, summary: string, detail: string) {
        this.messageToast.add({ severity: severity, summary: summary, detail: detail, life: 5000 });
    }
}
